import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
	Button,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	IconButton,
	MenuItem,
	Select,
	TextField,
	Typography,
} from '@material-ui/core';
import { Close } from '@material-ui/icons';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import {
	createNewAssignedRestrictionRule,
	deleteRestrictionRuleId,
	getAssignedRestrictionRules,
	updateExistingAssignedRestrictionRule,
} from '../../../../../../services/mediaService';
import { isoCountriesMap, isoCountryList } from '../../../../../../constants/languageConstants';

import './RestrictionRuleDialog.scss';
import { validateDomain, validateIPaddress } from '../../../../../../utils/commonUtil';
import { RestrictionRuleDeleteDialog } from '../RestrictionDeleteDialog/RestrictionRuleDeleteDialog';
import { useTranslation } from 'react-i18next';
import { showMessage } from '../../../../../../actions/globalActions';
import { messageTypes } from '../../../../../../constants/mediaConstants';

const RULE_ENUM = {
	IP: 'Ip',
	TOKEN: 'Token',
	GEO: 'Geo',
	DOMAIN: 'Domain',
};

type RestrictionRuleDialogProps = {
	open: boolean;
	onClose?: () => void;
};

const RestrictionRuleDialog = ({ open, onClose }: RestrictionRuleDialogProps) => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const { defaultAccountId } = useSelector((state) => (state as any).session);

	const [isNeedToFetchRules, setIsNeedToFetchRules] = useState<boolean>(true);

	const [restrictionRules, setRestrictionRules] = useState<any[]>([]);

	// ref rule only use to compare changes, update only when user change rule
	const [selectedRuleRef, setSelectedRuleRef] = useState<any>({ name: '', rules: [] });
	const [selectedRule, setSelectedRule] = useState<any>({ name: '', rules: [] });

	const [ipRangeFrom, setIpRangeFrom] = useState<string>('');
	const [ipRangeTo, setIpRangeTo] = useState<string>('');

	const [ipRangeFromError, setIpRangeFromError] = useState<boolean>(false);
	const [ipRangeToError, setIpRangeToError] = useState<boolean>(false);

	const [domainRestriction, setDomainRestriction] = useState<string>('');
	const [domainRestrictionError, setDomainRestrictionError] = useState<boolean>(false);

	const [deleteRuleDialogOpen, setDeleteRuleDialogOpen] = useState<boolean>(false);

	const closeDialog = () => {
		resetDialogState();
		onClose && onClose();
	};

	const resetDialogState = () => {
		setSelectedRule({ name: '', rules: [] });
		setSelectedRuleRef({ name: '', rules: [] });
		setIpRangeFrom('');
		setIpRangeTo('');
		setIpRangeFromError(false);
		setIpRangeToError(false);
		setDomainRestriction('');
	};

	const convertRulesArrayToMap = (rulesArray: any[]) => {
		return rulesArray.reduce((acc: any, rule: any) => {
			if (!acc[rule.type]) {
				acc[rule.type] = [rule];
			} else {
				acc[rule.type].push(rule);
			}

			return acc;
		}, {});
	};

	const convertMapToRulesArray = (rulesMap: any) => {
		const rulesArray: any[] = [];

		Object.keys(rulesMap).forEach((key) => {
			if (key === 'id' || key === 'name') {
				return;
			}
			rulesMap[key].forEach((rule: any) => {
				rulesArray.push(rule);
			});
		});

		return rulesArray;
	};

	const handleSelectedRuleChange = (ruleId: string) => {
		const isRuleModified = JSON.stringify(selectedRuleRef) !== JSON.stringify(selectedRule);

		if (isRuleModified) {
			const confirmChange = window.confirm('You have unsaved changes. Are you sure you want to switch rules?');
			if (!confirmChange) {
				return;
			}
		}

		resetDialogState();

		const item = restrictionRules.find((rule) => rule.id === ruleId);

		if (!item || !item.rules) {
			const newRule = { name: '' };
			setSelectedRule(newRule);
			setSelectedRuleRef(newRule);
			return;
		}

		const ruleMap = convertRulesArrayToMap(item.rules);

		const newRule = {
			...ruleMap,
			id: item.id,
			name: item.name,
		};

		setSelectedRule(newRule);
		setSelectedRuleRef(newRule);
	};

	const handleNewNameChange = (name: string) => {
		const newUpdateSelectedRule = {
			...selectedRule,
			name,
		};
		setSelectedRule(newUpdateSelectedRule);
	};

	const handleAddingIpRange = () => {
		const isFromIpValid = ipRangeFrom === '' || validateIPaddress(ipRangeFrom);
		const isToIpValid = ipRangeTo === '' || validateIPaddress(ipRangeTo);

		if (isFromIpValid && isToIpValid) {
			setIpRangeFrom('');
			setIpRangeTo('');

			const newIpRule = {
				type: RULE_ENUM.IP,
				from: ipRangeFrom,
				to: ipRangeTo,
			};

			const newUpdateSelectedRule = {
				...selectedRule,
				[RULE_ENUM.IP]: [...(selectedRule[RULE_ENUM.IP] || []), newIpRule],
			};
			setSelectedRule(newUpdateSelectedRule);
			return;
		}

		if (!isFromIpValid) {
			setIpRangeFromError(true);
			dispatch(
				showMessage(
					t('MEDIA_LIBRARY_MEDIA_INSPECT_PAGE_DETAILS_RESTRICTION_SET_VALID_FROM_IP_ADDRESS'),
					messageTypes.error
				)
			);
		}

		if (!isToIpValid) {
			setIpRangeToError(true);
			dispatch(
				showMessage(
					t('MEDIA_LIBRARY_MEDIA_INSPECT_PAGE_DETAILS_RESTRICTION_SET_VALID_TO_IP_ADDRESS'),
					messageTypes.error
				)
			);
		}
	};

	const handleRemovingIpRange = (index: number) => {
		const newIpRules = selectedRule[RULE_ENUM.IP].filter((_: any, i: number) => i !== index);

		if (newIpRules.length === 0) {
			const newUpdateSelectedRule = {
				...selectedRule,
			};
			delete newUpdateSelectedRule[RULE_ENUM.IP];
			setSelectedRule(newUpdateSelectedRule);
			return;
		}

		const newUpdateSelectedRule = {
			...selectedRule,
			[RULE_ENUM.IP]: newIpRules,
		};
		setSelectedRule(newUpdateSelectedRule);
	};

	const handleTokenChange = (secret: string) => {
		const newUpdateSelectedRule = {
			...selectedRule,
			[RULE_ENUM.TOKEN]: [{ type: RULE_ENUM.TOKEN, secret }],
		};

		if (secret === '') {
			delete newUpdateSelectedRule[RULE_ENUM.TOKEN];
		}

		setSelectedRule(newUpdateSelectedRule);
	};

	const handleAddingGeoRestriction = (countryCode: string) => {
		const existingLocations = selectedRule?.[RULE_ENUM.GEO]?.[0]?.location
			? selectedRule[RULE_ENUM.GEO][0].location.split(',').map((c: string) => c.trim())
			: [];

		const newLocations = [...existingLocations, countryCode];

		const newGeoRule = {
			type: RULE_ENUM.GEO,
			location: newLocations.join(', '),
		};

		const newUpdateSelectedRule = {
			...selectedRule,
			[RULE_ENUM.GEO]: [newGeoRule],
		};
		setSelectedRule(newUpdateSelectedRule);
	};

	const handleRemoveGeoRestriction = (countryCode: string) => {
		const existingLocations = selectedRule?.[RULE_ENUM.GEO]?.[0]?.location
			? selectedRule[RULE_ENUM.GEO][0].location.split(',').map((c: string) => c.trim())
			: [];

		const newLocations = existingLocations.filter((code: string) => code !== countryCode);

		if (newLocations.length === 0) {
			const newUpdateSelectedRule = {
				...selectedRule,
			};
			delete newUpdateSelectedRule[RULE_ENUM.GEO];
			setSelectedRule(newUpdateSelectedRule);
			return;
		}

		const newGeoRule = {
			type: RULE_ENUM.GEO,
			location: newLocations.join(', '),
		};

		const newUpdateSelectedRule = {
			...selectedRule,
			[RULE_ENUM.GEO]: [newGeoRule],
		};

		setSelectedRule(newUpdateSelectedRule);
	};

	const handleAddDomainRestriction = () => {
		const isDomainValid = domainRestriction && validateDomain(domainRestriction);

		if (isDomainValid) {
			const newDomainRule = {
				type: RULE_ENUM.DOMAIN,
				domains: [...(selectedRule[RULE_ENUM.DOMAIN]?.[0]?.domains || []), domainRestriction],
			};

			const newUpdateSelectedRule = {
				...selectedRule,
				[RULE_ENUM.DOMAIN]: [newDomainRule],
			};
			setSelectedRule(newUpdateSelectedRule);
			setDomainRestriction('');
			setDomainRestrictionError(false);
			return;
		}
		setDomainRestrictionError(true);
		dispatch(showMessage('Please enter a valid domain!', messageTypes.error));
	};

	const handleRemoveDomainRestriction = (index: number) => {
		const newDomains = selectedRule[RULE_ENUM.DOMAIN][0].domains.filter((_: any, i: number) => i !== index);

		if (newDomains.length === 0) {
			const newUpdateSelectedRule = {
				...selectedRule,
			};
			delete newUpdateSelectedRule[RULE_ENUM.DOMAIN];
			setSelectedRule(newUpdateSelectedRule);
			return;
		}

		const newDomainRule = {
			type: RULE_ENUM.DOMAIN,
			domains: newDomains,
		};

		const newUpdateSelectedRule = {
			...selectedRule,
			[RULE_ENUM.DOMAIN]: [newDomainRule],
		};
		setSelectedRule(newUpdateSelectedRule);
	};

	const handleDeleteRule = async () => {
		setDeleteRuleDialogOpen(false);
		const response = await deleteRestrictionRuleId(defaultAccountId, selectedRule.id);

		if (response && response.message) {
			dispatch(showMessage('Failed to delete rule!', messageTypes.error));
			return;
		}

		dispatch(showMessage('Rule deleted successfully!', messageTypes.success));
		resetDialogState();
		setIsNeedToFetchRules(true);
	};

	const handleUpdateBtnClicked = () => {
		const isCreateNewRule = !selectedRule.id;

		if (isCreateNewRule) {
			handleCreateNewRule();
		} else {
			handleUpdateRule();
		}
	};

	const handleUpdateRule = async () => {
		const updateRulePayload = {
			name: selectedRule.name,
			rules: convertMapToRulesArray(selectedRule),
		};

		if (updateRulePayload.rules.length === 0) {
			dispatch(showMessage('Please add at least one restriction rule!', messageTypes.warning));
			return;
		}

		const response = await updateExistingAssignedRestrictionRule(
			defaultAccountId,
			selectedRule.id,
			updateRulePayload
		);

		if (response && response.message) {
			dispatch(showMessage('Failed to update rule!', messageTypes.error));
			return;
		}

		dispatch(showMessage('Rule updated successfully!', messageTypes.success));
		setIsNeedToFetchRules(true);
	};

	const handleCreateNewRule = async () => {
		const newRulePayload = {
			name: selectedRule.name,
			rules: convertMapToRulesArray(selectedRule),
		};

		if (newRulePayload.rules.length === 0) {
			dispatch(showMessage('Please add at least one restriction rule!', messageTypes.warning));
			return;
		}

		const response = await createNewAssignedRestrictionRule(defaultAccountId, newRulePayload);

		if (response && response.message) {
			dispatch(showMessage('Failed to create new rule!', messageTypes.error));
			return;
		}

		dispatch(showMessage('New rule created successfully!', messageTypes.success));
		setIsNeedToFetchRules(true);
		resetDialogState();
	};

	useEffect(() => {
		const fetchRestrictionRules = async () => {
			const data = await getAssignedRestrictionRules(defaultAccountId);

			if (!data && data.length === 0) {
				return;
			}

			const rules: any[] = [];

			data.forEach((item: any) => {
				let isValidRule = false;

				if (item.rules) {
					item.rules.map((r: any) => {
						if (Object.values(RULE_ENUM).indexOf(r.type) >= 0) {
							isValidRule = true;
						}
					});

					if (isValidRule && item.hasOwnProperty('name')) {
						//displaying only if rule is having a name
						rules.push(item);
					}
				}
			});

			rules.sort((a, b) => a.name.localeCompare(b.name));
			setRestrictionRules(rules);

			if (selectedRule.id) {
				const updatedSelectedRule = rules.find((rule) => rule.id === selectedRule.id);
				if (updatedSelectedRule) {
					const ruleMap = convertRulesArrayToMap(updatedSelectedRule.rules);

					const newRule = {
						...ruleMap,
						id: updatedSelectedRule.id,
						name: updatedSelectedRule.name,
					};

					setSelectedRule(newRule);
					setSelectedRuleRef(newRule);
				}
			}
			setIsNeedToFetchRules(false);
		};

		if (!isNeedToFetchRules || !defaultAccountId) {
			return;
		}

		// BE is slow so delay 1s before fetching to avoid missing data
		setTimeout(() => {
			fetchRestrictionRules();
		}, 1000);
	}, [defaultAccountId, isNeedToFetchRules]);

	return (
		<>
			<Dialog open={open} fullWidth={true} maxWidth="sm" className="restriction-rule-dialog">
				<DialogTitle className="restriction-rule-dialog__title-wrapper" disableTypography>
					<Typography variant="h6" className="restriction-field-title">
						Manage & create new rule
					</Typography>
					<IconButton size="small" onClick={closeDialog}>
						<Close />
					</IconButton>
				</DialogTitle>
				<DialogContent className="restriction-rule-dialog__content-wrapper">
					<div className="restriction-rules-section border-bottom">
						<Typography
							variant="subtitle1"
							className="restriction-rules-section__title restriction-field-title"
						>
							Select existing rule or create new
						</Typography>
						<FormControl size="small" style={{ minWidth: '100%' }}>
							<Select
								IconComponent={KeyboardArrowDownIcon}
								variant="outlined"
								onChange={(event) => {
									handleSelectedRuleChange(event.target.value as string);
								}}
								defaultValue="create-a-new-rule"
								value={selectedRule?.id || 'create-a-new-rule'}
								disabled={isNeedToFetchRules}
							>
								<MenuItem key="create-a-new-rule" value="create-a-new-rule">
									+ Create new
								</MenuItem>
								{restrictionRules.map((rule) => (
									<MenuItem key={rule.id} value={rule.id}>
										{rule.name}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</div>
					{!selectedRule?.id && (
						<div className="restriction-rules-section">
							<Typography
								variant="subtitle1"
								className="restriction-rules-section__title restriction-field-title"
							>
								Name
							</Typography>
							<FormControl style={{ minWidth: '100%' }}>
								<TextField
									variant="outlined"
									defaultValue=""
									size="small"
									value={selectedRule?.name || ''}
									onChange={(event) => {
										handleNewNameChange(event.target.value);
									}}
									disabled={isNeedToFetchRules}
								/>
							</FormControl>
						</div>
					)}

					<div className="restriction-rules-section">
						<Typography
							variant="subtitle1"
							className="restriction-rules-section__title restriction-field-title"
						>
							IP range (from - to)
						</Typography>
						<div className="ip-range-restriction">
							<TextField
								className="ip-range-field"
								variant="outlined"
								size="small"
								value={ipRangeFrom}
								placeholder="xxx.xxx.xxx.xxx"
								onChange={(event) => {
									setIpRangeFrom(event.target.value);
								}}
								error={ipRangeFromError}
								disabled={isNeedToFetchRules}
							/>
							-
							<TextField
								className="ip-range-field"
								variant="outlined"
								size="small"
								value={ipRangeTo}
								placeholder="xxx.xxx.xxx.xxx"
								onChange={(event) => {
									setIpRangeTo(event.target.value);
								}}
								error={ipRangeToError}
								disabled={isNeedToFetchRules}
							/>
							<Button
								variant="contained"
								color="primary"
								disabled={(!ipRangeFrom && !ipRangeTo) || isNeedToFetchRules}
								onClick={() => {
									setIpRangeFromError(false);
									setIpRangeToError(false);
									handleAddingIpRange();
								}}
								style={{
									whiteSpace: 'nowrap',
									minWidth: 'auto',
								}}
							>
								+ Add
							</Button>
						</div>
						<div className="submitted-data ip-range-submit">
							{selectedRule &&
								selectedRule[RULE_ENUM.IP] &&
								selectedRule[RULE_ENUM.IP].length > 0 &&
								selectedRule[RULE_ENUM.IP].map((rule: any, index: number) => (
									<Chip
										key={`${index} - ${rule.from} - ${rule.to}`}
										className="submitted-data__chip"
										label={`${rule.from} - ${rule.to}`}
										deleteIcon={<Close style={{ width: 18, height: 18, fill: '#ffffff ' }} />}
										onDelete={() => {
											handleRemovingIpRange(index);
										}}
										disabled={isNeedToFetchRules}
									/>
								))}
						</div>
					</div>
					<div className="restriction-rules-section">
						<Typography
							variant="subtitle1"
							className="restriction-rules-section__title restriction-field-title"
						>
							Token
						</Typography>
						<FormControl style={{ minWidth: '100%' }}>
							<TextField
								variant="outlined"
								defaultValue=""
								size="small"
								value={selectedRule?.[RULE_ENUM.TOKEN]?.[0]?.secret || ''}
								onChange={(event) => {
									handleTokenChange(event.target.value);
								}}
								disabled={isNeedToFetchRules}
							/>
						</FormControl>
					</div>
					<div className="restriction-rules-section">
						<Typography
							variant="subtitle1"
							className="restriction-rules-section__title restriction-field-title"
						>
							Geographical restriction
						</Typography>
						<Typography variant="subtitle1" className="restriction-rules-section__title">
							To allow
						</Typography>
						<FormControl size="small" style={{ minWidth: '100%' }}>
							<Select
								defaultValue="-Select-"
								value="-Select-"
								IconComponent={KeyboardArrowDownIcon}
								variant="outlined"
								onChange={(event) => {
									handleAddingGeoRestriction(event.target.value as string);
								}}
								disabled={isNeedToFetchRules}
							>
								{isoCountryList.map(({ name, code }: any) => {
									const isAlreadySelected =
										selectedRule?.[RULE_ENUM.GEO]?.[0]?.location
											?.split(',')
											.map((c: string) => c.trim())
											.indexOf(code) >= 0;
									return (
										<MenuItem key={code} value={code} disabled={isAlreadySelected}>
											{name}
										</MenuItem>
									);
								})}
							</Select>
						</FormControl>
						<div className="submitted-data geographical-submit">
							{selectedRule?.[RULE_ENUM.GEO]?.[0]?.location &&
								selectedRule[RULE_ENUM.GEO][0].location.split(',').map((countryCode: string) => {
									const countryName =
										isoCountriesMap[countryCode.trim() as keyof typeof isoCountriesMap];

									if (!countryName) {
										return null;
									}

									return (
										<Chip
											key={countryCode}
											className="submitted-data__chip"
											label={countryName}
											deleteIcon={<Close style={{ width: 18, height: 18, fill: '#ffffff ' }} />}
											onDelete={() => {
												handleRemoveGeoRestriction(countryCode.trim());
											}}
											disabled={isNeedToFetchRules}
										/>
									);
								})}
						</div>
					</div>
					<div className="restriction-rules-section">
						<Typography
							variant="subtitle1"
							className="restriction-rules-section__title restriction-field-title"
						>
							Domain restriction
						</Typography>
						<Typography variant="subtitle1" className="restriction-rules-section__title">
							To allow
						</Typography>
						<div className="domain-restriction">
							<TextField
								className="domain-field"
								variant="outlined"
								defaultValue="Hello World"
								size="small"
								value={domainRestriction}
								onChange={(event) => setDomainRestriction(event.target.value)}
								error={domainRestrictionError}
								disabled={isNeedToFetchRules}
							/>
							<Button
								variant="contained"
								color="primary"
								disabled={!domainRestriction || isNeedToFetchRules}
								onClick={handleAddDomainRestriction}
								style={{
									whiteSpace: 'nowrap',
									minWidth: 'auto',
								}}
							>
								+ Add
							</Button>
						</div>
						<div className="submitted-data domain-submit">
							{selectedRule?.[RULE_ENUM.DOMAIN]?.[0]?.domains &&
								selectedRule[RULE_ENUM.DOMAIN][0].domains.map((domain: string, index: number) => (
									<Chip
										key={`${domain} - ${index}`}
										className="submitted-data__chip"
										label={domain}
										deleteIcon={<Close style={{ width: 18, height: 18, fill: '#ffffff ' }} />}
										onDelete={() => {
											handleRemoveDomainRestriction(index);
										}}
										disabled={isNeedToFetchRules}
									/>
								))}
						</div>
					</div>
				</DialogContent>
				<DialogActions className="restriction-rule-dialog__actions-wrapper">
					<Button
						className="delete-action-btn"
						onClick={() => {
							setDeleteRuleDialogOpen(true);
						}}
						color="secondary"
						variant="outlined"
						disabled={!selectedRule?.id || isNeedToFetchRules}
					>
						Delete
					</Button>
					<Button onClick={closeDialog} color="primary" variant="outlined" disabled={isNeedToFetchRules}>
						Cancel
					</Button>
					<Button
						onClick={handleUpdateBtnClicked}
						color="primary"
						variant="contained"
						disabled={
							(!selectedRule.id && !selectedRule.name) ||
							JSON.stringify(selectedRuleRef) === JSON.stringify(selectedRule) ||
							isNeedToFetchRules
						}
					>
						{isNeedToFetchRules ? (
							<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
								Updating... <CircularProgress size={10} color="inherit" />
							</div>
						) : (
							'Update'
						)}
					</Button>
				</DialogActions>
			</Dialog>
			<RestrictionRuleDeleteDialog
				t={t}
				open={deleteRuleDialogOpen}
				deleteSelected={handleDeleteRule}
				handleHideDialog={() => {
					setDeleteRuleDialogOpen(false);
				}}
			/>
		</>
	);
};

export default RestrictionRuleDialog;
